import { forwardRef } from 'react';
import { cn } from '@/lib/cn';

/**
 * Boolean toggle.
 *
 * Deliberately dependency-free (a `<button role="switch">` rather than another
 * Radix package) because that is all the semantics a switch needs. Replaces the
 * ad-hoc dot-and-label buttons that each settings page had reinvented, none of
 * which looked like a switch.
 */
export const Switch = forwardRef<
  HTMLButtonElement,
  {
    checked: boolean;
    onCheckedChange: (next: boolean) => void;
    disabled?: boolean;
    /** Required unless the switch is wired to a visible `<label>` via id. */
    'aria-label'?: string;
    'aria-labelledby'?: string;
    id?: string;
    className?: string;
  }
>(function Switch({ checked, onCheckedChange, disabled, className, ...rest }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cn(
        'press relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border border-transparent transition-colors duration-150 ease-out focus-ring',
        'disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-primary' : 'bg-muted-foreground/30',
        className,
      )}
      {...rest}
    >
      <span
        aria-hidden
        className={cn(
          'inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform duration-150 ease-out',
          checked ? 'translate-x-[1.15rem]' : 'translate-x-[0.15rem]',
        )}
      />
    </button>
  );
});
